import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetcher } from 'react-router';
import Alert from '~/components/Alert';
import Submit from '~/components/button/Submit';
import DescriptionList, {
  DescriptionListItem,
} from '~/components/DescriptionList';
import Divide from '~/components/Divide';
import Form from '~/components/Form';
import Page from '~/components/layout/page/Page';
import LineItem from '~/components/LineItem';
import LoadingBoundary from '~/components/LoadingBoundary';
import PaymentFields from '~/components/PaymentFields';
import { messagingInDays, useCurrencyFormatter } from '~/utils/formatters';
import { useIsLoading } from '~/utils/routing';
import {
  extractSectionSummaryLabel,
  extractSectionSummaryValue,
} from '~/utils/summary';
import {
  ClaimValues,
  FormKey,
  useActionResult,
  useLoaderData,
} from './returnSummary';
import ReturnSummaryDisclosure from './ReturnSummaryDisclosure';
import Selection from './ReturnSummarySelection';

type ReturnSummary = NonNullable<ReturnType<typeof useLoaderData>>;
type ReturnSummaryLineItem = ReturnSummary['credits']['lineItems'][number];
type Section = ReturnSummary['credits']['sections'][number];

const useReturnSummaryFetcher = () => {
  //! able to set the generic to ReturnSummary because the fetcher submits with GET to the same endpoint as the loader
  const fetcher = useFetcher<ReturnSummary>({ key: 'return-summary-fetcher' });
  const submit = useCallback(
    (value: Partial<Record<FormKey, string>>) => {
      // oxlint-disable-next-line @typescript-eslint/no-floating-promises
      fetcher.submit(value, { method: 'GET', preventScrollReset: true });
    },
    [fetcher],
  );

  return useMemo(
    () => ({
      state: fetcher.state,
      data: fetcher.data,
      submit,
    }),
    [fetcher, submit],
  );
};

const useReturnSummaryData = () => {
  const fetcher = useReturnSummaryFetcher();
  const loaderData = useLoaderData();

  return fetcher.data ?? loaderData;
};

// -------- Helper Functions --------
const pluralizeTitle = (count: number, title: string) =>
  `${title}${count > 1 ? 's' : ''} (${count})`;

// -------- Components --------

function Sections({ sections }: { sections: Section[] }) {
  const currencyFormatter = useCurrencyFormatter();
  const sectionsWithAmount = sections.filter((section) => section.amount !== 0);

  return (
    <>
      {sectionsWithAmount.map((section) => (
        <div className="flex justify-between p-2" key={section.label}>
          <span>{section.label}</span>
          <span>{currencyFormatter(Math.abs(section.amount))}</span>
        </div>
      ))}
    </>
  );
}

function LineItems({
  lineItems,
  title,
}: {
  lineItems: ReturnSummaryLineItem[];
  title: string;
}) {
  const currencyFormatter = useCurrencyFormatter();

  if (lineItems.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 rounded-md bg-surface-container-low p-2">
      <p className="body-md">{pluralizeTitle(lineItems.length, title)}</p>
      {lineItems.map(
        ({ unitPrice, imgUrl, name, quantity, optionsFromPlatform }, index) => (
          <LineItem
            /* oxlint-disable-next-line react/no-array-index-key */ // TODO adjust API to provide a unique ID that can be used as a key
            key={index}
            name={name ?? ''}
            price={`${currencyFormatter(unitPrice)} x ${quantity}`}
            imgSrc={imgUrl}
            quantity={quantity}
            options={optionsFromPlatform}
            variant="small"
          />
        ),
      )}
    </div>
  );
}

function MonetarySection({
  outstandingBalance,
  selectedExchangeMethod,
  refundAmount,
  timeToReturnBeforeCharging,
}: {
  outstandingBalance: ReturnSummary['outstandingBalance'];
  selectedExchangeMethod: ReturnSummary['exchangeMethod']['selected'];
  refundAmount: number;
  timeToReturnBeforeCharging: number;
}) {
  const currencyFormatter = useCurrencyFormatter();
  if (!outstandingBalance) return null;

  const instantExchangeAlert =
    selectedExchangeMethod?.type === 'instant'
      ? `A hold of ${currencyFormatter(refundAmount)} will be placed on your card.
         The hold will be removed if your items are shipped within ${messagingInDays(
           timeToReturnBeforeCharging,
         )}. Otherwise you will be charged for the hold amount.`
      : null;

  return (
    <div className="flex flex-col gap-4">
      <DescriptionList>
        <DescriptionListItem
          label={outstandingBalance.label}
          value={currencyFormatter(Math.abs(outstandingBalance.total))}
        />
      </DescriptionList>
      {instantExchangeAlert && (
        <div className="body-sm text-on-surface-variant">
          {instantExchangeAlert}
        </div>
      )}
    </div>
  );
}

const lineItemStyles =
  'flex flex-col gap-2 rounded-md bg-surface-container-low body-md text-on-surface-variant';

function ReturnCredit({ credits }: { credits: ReturnSummary['credits'] }) {
  const currencyFormatter = useCurrencyFormatter();
  const { total, sections, lineItems } = credits;

  return (
    <ReturnSummaryDisclosure
      label="Return Credit"
      value={currencyFormatter(Math.abs(total))}
    >
      <div className={lineItemStyles}>
        <LineItems lineItems={lineItems} title="Return Item" />
        <Sections sections={sections} />
      </div>
    </ReturnSummaryDisclosure>
  );
}

function Deductions({ deductions }: { deductions: ReturnSummary['credits'] }) {
  const currencyFormatter = useCurrencyFormatter();
  const { total, sections, lineItems } = deductions;

  return (
    <ReturnSummaryDisclosure
      label="Deductions"
      value={currencyFormatter(total)}
      defaultOpen
    >
      <div className={lineItemStyles}>
        <LineItems lineItems={lineItems} title="Exchange Item" />
        <Sections sections={sections} />
      </div>
    </ReturnSummaryDisclosure>
  );
}

function Content() {
  const {
    credits,
    deductions,
    authorizedAmount,
    outstandingBalance,
    balance,
    creditMethod: {
      selected: selectedCreditMethod,
      offered: offeredCreditMethods,
    },
    exchangeMethod: {
      selected: selectedExchangeMethod,
      offered: offeredExchangeMethods,
    },
    timeToReturnBeforeCharging,
    isOriginatingOrderCredit,
  } = useReturnSummaryData();
  const actionResult = useActionResult();
  const isLoading = useIsLoading();
  const { submit: fetchUpdates, state: fetchUpdatesState } =
    useReturnSummaryFetcher();

  // TODO: an error scenario would be a type === 'payment' but no paymentIntentSecret
  const paymentRequired = outstandingBalance?.kind === 'Payment';
  const [paymentValid, setPaymentValid] = useState(false);
  const exchangeSelectionRequired =
    offeredExchangeMethods.length > 1 && !selectedExchangeMethod;
  // the amount is traced after submission, so a $0 balance still needs the customer's preference
  const shouldDisplayCreditSelection =
    offeredCreditMethods.length > 1 &&
    (isOriginatingOrderCredit || Number(outstandingBalance?.total) < 0);

  // * paymentRequired is derived from the loader data that can change based on user selections, so we need to reset paymentValid whenever paymentRequired changes
  useEffect(() => {
    setPaymentValid(false);
  }, [paymentRequired]);

  return (
    <Form method="POST" name="return-summary-form">
      <Divide>
        <ReturnCredit credits={credits} />

        {/* a $0 exchange still has an item to show, so gate on content not amount */}
        {(deductions.total > 0 || deductions.lineItems.length > 0) && (
          <Deductions deductions={deductions} />
        )}

        <Selection
          name={FormKey.exchangeMethod}
          label="Exchange Options"
          selected={selectedExchangeMethod}
          options={offeredExchangeMethods}
          onChange={({ type }) =>
            fetchUpdates({ [FormKey.exchangeMethod]: type })
          }
          disabled={fetchUpdatesState === 'loading'}
        />

        {shouldDisplayCreditSelection && (
          <Selection
            name={FormKey.creditMethod}
            label="Refund Method"
            selected={selectedCreditMethod}
            options={offeredCreditMethods}
            onChange={({ type }) =>
              fetchUpdates({ [FormKey.creditMethod]: type })
            }
            disabled={fetchUpdatesState === 'loading'}
          />
        )}

        <div>
          <MonetarySection
            outstandingBalance={outstandingBalance}
            refundAmount={authorizedAmount - balance}
            selectedExchangeMethod={selectedExchangeMethod}
            timeToReturnBeforeCharging={timeToReturnBeforeCharging}
          />
          {paymentRequired && (
            <>
              <PaymentFields
                className="mt-4"
                onChange={(event) => {
                  setPaymentValid(
                    !event.empty && !event.error && event.complete,
                  );
                }}
              />
              <input
                hidden
                type="checkbox"
                name={FormKey.paymentRequired}
                defaultChecked
              />
            </>
          )}
        </div>
      </Divide>

      {actionResult?.ok === false && (
        <Alert variant="error" title={actionResult.message} />
      )}

      <Submit
        // TODO disable submit if payment required, but no payment can be collected
        disabled={
          exchangeSelectionRequired ||
          fetchUpdatesState !== 'idle' ||
          (paymentRequired && !paymentValid)
        }
        loading={isLoading}
      >
        Submit Return Request
      </Submit>

      <input
        type="hidden"
        name={FormKey.claimValues}
        value={JSON.stringify({
          fromCustomerShipping: extractSectionSummaryValue(
            'RETURN_SHIPPING',
            deductions.sections,
          ),
          fee: extractSectionSummaryValue('FEE', deductions.sections),
          feeName: extractSectionSummaryLabel('FEE', deductions.sections),
          incentive: extractSectionSummaryValue('INCENTIVE', credits.sections),
          balance,
          toCustomerShipping: extractSectionSummaryValue(
            'SHIPPING',
            deductions.sections,
          ),
          authorizedAmount,
        } satisfies ClaimValues)}
      />
    </Form>
  );
}

const defaultMessages = [
  'Submitting your request',
  'Getting your shipping label',
  'Working on it',
  'Almost done',
];

export default function ReturnSummaryPage() {
  const isLoading = useIsLoading();

  return (
    <Page.Root title="Return Summary">
      <LoadingBoundary isLoading={isLoading} loadingMessages={defaultMessages}>
        <Content />
      </LoadingBoundary>
    </Page.Root>
  );
}
